import { analyzePortfolioPosition } from "../analysis/portfolioPositionAnalysis";
import { analyzeDecisionFrontier } from "../frontier/decisionFrontier";
import { createExecutiveDecisionSet } from "../decision_set/executiveDecisionSet";
import { applyDecisionPolicy } from "../policy/decisionPolicy";
import { createGovernanceDecision } from "../governance/governanceDecision";

const PORTFOLIO_DECISION_PIPELINE_VERSION = "1.0.0";

const STAGES = [
  "PORTFOLIO_POSITION",
  "DECISION_FRONTIER",
  "EXECUTIVE_DECISION_SET",
  "DECISION_POLICY",
  "GOVERNANCE_DECISION",
];

function isUsablePlainObject(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function safeErrorMessage(error) {
  if (error && typeof error.message === "string") {
    return error.message;
  }

  try {
    return String(error);
  } catch {
    return "Unknown stage execution error";
  }
}

function isComplete(policyDecision, governanceDecision, completedStages) {
  const readinessStatus = governanceDecision?.readiness?.status;

  return (
    completedStages.length === STAGES.length &&
    policyDecision?.decision?.status === "SELECTED" &&
    policyDecision?.selectedAlternative !== null &&
    policyDecision?.selectedAlternative !== undefined &&
    governanceDecision !== null &&
    governanceDecision?.evidence?.isComplete === true &&
    (readinessStatus === "READY_FOR_GOVERNANCE" ||
      readinessStatus === "GOVERNANCE_ACTION_REQUIRED")
  );
}

export function executePortfolioDecision(input = {}) {
  const inputWasExplicitlyUndefined =
    arguments.length > 0 && arguments[0] === undefined;
  const validInput =
    !inputWasExplicitlyUndefined && isUsablePlainObject(input);
  const source = validInput ? input : {};
  const completedStages = [];
  const unavailableStages = [];
  const errors = [];

  let portfolioPosition = null;
  let decisionFrontier = null;
  let executiveDecisionSet = null;
  let policyDecision = null;
  let governanceDecision = null;

  function executeStage(stage, operation) {
    try {
      const result = operation();
      completedStages.push(stage);
      return result;
    } catch (error) {
      unavailableStages.push(stage);
      errors.push({
        stage,
        code: "STAGE_EXECUTION_FAILED",
        message: safeErrorMessage(error),
      });
      return null;
    }
  }

  portfolioPosition = executeStage("PORTFOLIO_POSITION", () =>
    analyzePortfolioPosition(source.portfolioPositionInput ?? {})
  );

  decisionFrontier = executeStage("DECISION_FRONTIER", () =>
    analyzeDecisionFrontier(source.alternatives ?? [])
  );

  if (decisionFrontier !== null) {
    executiveDecisionSet = executeStage("EXECUTIVE_DECISION_SET", () =>
      createExecutiveDecisionSet(decisionFrontier)
    );
  } else {
    unavailableStages.push("EXECUTIVE_DECISION_SET");
  }

  if (executiveDecisionSet !== null) {
    policyDecision = executeStage("DECISION_POLICY", () =>
      applyDecisionPolicy(executiveDecisionSet, source.decisionPolicy ?? {})
    );
  } else {
    unavailableStages.push("DECISION_POLICY");
  }

  if (policyDecision !== null) {
    governanceDecision = executeStage("GOVERNANCE_DECISION", () =>
      createGovernanceDecision(
        policyDecision,
        source.governancePolicy ?? {},
        source.governanceEvidence ?? {}
      )
    );
  } else {
    unavailableStages.push("GOVERNANCE_DECISION");
  }

  const status =
    !validInput || completedStages.length === 0
      ? "UNAVAILABLE"
      : isComplete(policyDecision, governanceDecision, completedStages)
      ? "COMPLETE"
      : "PARTIAL";
  const metadataSource = isUsablePlainObject(source.metadata)
    ? source.metadata
    : {};

  return {
    portfolioPosition,
    decisionFrontier,
    executiveDecisionSet,
    policyDecision,
    governanceDecision,
    execution: {
      status,
      completedStages,
      unavailableStages,
      errors,
    },
    metadata: {
      pipelineVersion: PORTFOLIO_DECISION_PIPELINE_VERSION,
      executedAt: Object.prototype.hasOwnProperty.call(
        metadataSource,
        "executedAt"
      )
        ? metadataSource.executedAt
        : null,
    },
  };
}
